import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { Ruolo } from '@/types/entities/user.ts';

type ModalInstance = { show(): void; hide(): void; dispose(): void };

export class GestioneUtenti extends AppComponent {
  static override componentName = 'wc-gestione-utenti';

  protected override get templateId() { return 'gestione-utenti'; }

  private currentUserId = 0;
  private modals: ModalInstance[] = [];

  protected override afterRender(): void {
    this.currentUserId = this.attributeNumber('data-current-user-id');
    this.appendModals();
    this.setupActions();
    this.caricaLista();
  }

  private appendModals(): void {
    const modali = this.$$('[data-modal]');
    for (const el of modali) {
      document.body.appendChild(el);
    }
  }

  private getModal(selector: string): ModalInstance | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  private setupActions(): void {
    this.delegate.on('click', '[data-action="nuovo-utente"]', () => this.apriModaleNuovo());
    this.delegate.on('click', '[data-action="modifica-utente"]', (e: Event) => this.apriModaleModifica(e));
    this.delegate.on('click', '[data-action="cambia-ruolo"]', (e: Event) => this.apriModaleRuolo(e));
    this.delegate.on('click', '[data-action="reset-password"]', (e: Event) => this.apriModalePassword(e));
    this.delegate.on('click', '[data-action="toggle-stato"]', (e: Event) => this.handleToggleStato(e));
    this.delegate.on('click', '[data-action="sync-fenice"]', () => this.apriModaleSync());

    this.listen(document, 'click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-action="conferma-sync"]')) this.handleSync();
    });
    this.listen(document, 'change', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('[data-action="seleziona-tutti"]')) {
        this.handleSelezionaTutti((target as HTMLInputElement).checked);
      }
      if (target.matches('[data-sync-check]')) {
        this.aggiornaContatoreSync();
      }
    });

    this.listen(document, 'submit', (e: Event) => {
      const form = e.target as HTMLFormElement;
      if (form.matches('[data-form="nuovo"]')) { e.preventDefault(); this.handleSubmitNuovo(); }
      if (form.matches('[data-form="modifica"]')) { e.preventDefault(); this.handleSubmitModifica(); }
      if (form.matches('[data-form="ruolo"]')) { e.preventDefault(); this.handleSubmitRuolo(); }
      if (form.matches('[data-form="password"]')) { e.preventDefault(); this.handleSubmitPassword(); }
    });

    this.listen(document, 'focusout', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.matches('#modalNuovoUtente [data-field="email"]')) {
        this.lookupFeniceNuovo();
      }
    });
  }

  // --- Caricamento lista ---

  private async caricaLista(): Promise<void> {
    const container = this.$('[data-results]');
    if (!container) return;

    templateParseToElement('gestione-utenti-tabella', container, { loading: true });

    const result = await this.api.utenti.lista();
    if (!result.ok) {
      templateParseToElement('gestione-utenti-tabella', container, { error: result.error });
      return;
    }

    const { utenti, count } = result.data;
    templateParseToElement('gestione-utenti-tabella', container, {
      utenti, count, currentUserId: this.currentUserId,
    });
  }

  // --- Modale Nuovo Utente ---

  private apriModaleNuovo(): void {
    const modal = this.getModal('#modalNuovoUtente');
    if (!modal) return;
    this.resetForm('#modalNuovoUtente');
    modal.show();
  }

  private async handleSubmitNuovo(): Promise<void> {
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;

    const email = (root.querySelector('[data-field="email"]') as HTMLInputElement).value.trim();
    const nome = (root.querySelector('[data-field="nome"]') as HTMLInputElement).value.trim();
    const cognome = (root.querySelector('[data-field="cognome"]') as HTMLInputElement).value.trim();
    const password = (root.querySelector('[data-field="password"]') as HTMLInputElement).value;
    const conferma = (root.querySelector('[data-field="conferma"]') as HTMLInputElement).value;
    const ruolo = (root.querySelector('[data-field="ruolo"]:checked') as HTMLInputElement)?.value as Ruolo || 'operatore';
    const idFeniceVal = (root.querySelector('[data-field="id-fenice"]') as HTMLInputElement)?.value;
    const idFenice = idFeniceVal ? Number(idFeniceVal) : null;

    const errore = this.validaNuovo(email, nome, cognome, password, conferma);
    if (errore) {
      this.mostraValidationError(root, errore);
      return;
    }

    const result = await this.api.utenti.crea({ email, password, nome, cognome, ruolo, idFenice });
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    this.getModal('#modalNuovoUtente')?.hide();
    Weext.toast('Utente creato', { type: 'success' });
    this.caricaLista();
  }

  private validaNuovo(email: string, nome: string, cognome: string, password: string, conferma: string): string | null {
    if (!email || !nome || !cognome || !password) return 'Tutti i campi sono obbligatori';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Formato email non valido';
    if (password.length < 6) return 'La password deve avere almeno 6 caratteri';
    if (password !== conferma) return 'Le password non coincidono';
    return null;
  }

  // --- Modale Modifica Anagrafica ---

  private apriModaleModifica(e: Event): void {
    const btn = (e.target as HTMLElement).closest('[data-action="modifica-utente"]') as HTMLElement;
    const id = btn?.dataset.id;
    const nome = btn?.dataset.nome;
    const cognome = btn?.dataset.cognome;
    const email = btn?.dataset.email ?? '';
    if (!id) return;

    const root = document.querySelector('#modalModificaUtente');
    if (!root) return;

    const idEl = root.querySelector('[data-modifica-id]') as HTMLInputElement | null;
    if (idEl) idEl.value = id;

    const nomeEl = root.querySelector('[data-field="modifica-nome"]') as HTMLInputElement | null;
    if (nomeEl) nomeEl.value = nome ?? '';

    const cognomeEl = root.querySelector('[data-field="modifica-cognome"]') as HTMLInputElement | null;
    if (cognomeEl) cognomeEl.value = cognome ?? '';

    const idFenice = btn?.dataset.idFenice;
    const idFeniceEl = root.querySelector('[data-field="modifica-id-fenice"]') as HTMLInputElement | null;
    if (idFeniceEl) idFeniceEl.value = idFenice && idFenice !== 'null' ? idFenice : '';

    // Reset stato indicatore Fenice
    const statusEl = root.querySelector('[data-fenice-status]') as HTMLElement | null;
    if (statusEl) statusEl.classList.add('d-none');

    this.nascondiValidationError(root);
    this.getModal('#modalModificaUtente')?.show();

    // Se id_fenice non presente, lookup automatico da Fenice
    if (!idFenice || idFenice === 'null') {
      this.lookupFeniceModifica(root, email);
    }
  }

  private async handleSubmitModifica(): Promise<void> {
    const root = document.querySelector('#modalModificaUtente');
    if (!root) return;

    const id = Number((root.querySelector('[data-modifica-id]') as HTMLInputElement).value);
    const nome = (root.querySelector('[data-field="modifica-nome"]') as HTMLInputElement).value.trim();
    const cognome = (root.querySelector('[data-field="modifica-cognome"]') as HTMLInputElement).value.trim();
    const idFeniceVal = (root.querySelector('[data-field="modifica-id-fenice"]') as HTMLInputElement)?.value;
    const idFenice = idFeniceVal ? Number(idFeniceVal) : undefined;

    if (!nome || !cognome) {
      this.mostraValidationError(root, 'Nome e cognome sono obbligatori');
      return;
    }

    const result = await this.api.utenti.modificaAnagrafica(id, nome, cognome, idFenice);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    this.getModal('#modalModificaUtente')?.hide();
    Weext.toast('Dati aggiornati', { type: 'success' });
    this.caricaLista();
  }

  // --- Modale Cambio Ruolo ---

  private apriModaleRuolo(e: Event): void {
    const btn = (e.target as HTMLElement).closest('[data-action="cambia-ruolo"]') as HTMLElement;
    const id = btn?.dataset.id;
    const ruoloAttuale = btn?.dataset.ruoloAttuale;
    const nome = btn?.dataset.nome;
    if (!id) return;

    const root = document.querySelector('#modalCambiaRuolo');
    if (!root) return;

    const nomeEl = root.querySelector('[data-ruolo-nome]');
    if (nomeEl) nomeEl.textContent = nome ?? '';

    const idEl = root.querySelector('[data-ruolo-id]') as HTMLInputElement | null;
    if (idEl) idEl.value = id;

    const radios = root.querySelectorAll('[data-field="nuovo-ruolo"]') as NodeListOf<HTMLInputElement>;
    for (const r of radios) {
      r.checked = r.value === ruoloAttuale;
    }

    this.getModal('#modalCambiaRuolo')?.show();
  }

  private async handleSubmitRuolo(): Promise<void> {
    const root = document.querySelector('#modalCambiaRuolo');
    if (!root) return;

    const id = Number((root.querySelector('[data-ruolo-id]') as HTMLInputElement).value);
    const nuovoRuolo = (root.querySelector('[data-field="nuovo-ruolo"]:checked') as HTMLInputElement)?.value as Ruolo;
    if (!id || !nuovoRuolo) return;

    const result = await this.api.utenti.cambiaRuolo(id, nuovoRuolo);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    this.getModal('#modalCambiaRuolo')?.hide();
    Weext.toast('Ruolo aggiornato', { type: 'success' });
    this.caricaLista();
  }

  // --- Modale Reset Password ---

  private apriModalePassword(e: Event): void {
    const btn = (e.target as HTMLElement).closest('[data-action="reset-password"]') as HTMLElement;
    const id = btn?.dataset.id;
    const nome = btn?.dataset.nome;
    if (!id) return;

    const root = document.querySelector('#modalResetPassword');
    if (!root) return;

    const nomeEl = root.querySelector('[data-password-nome]');
    if (nomeEl) nomeEl.textContent = nome ?? '';

    const idEl = root.querySelector('[data-password-id]') as HTMLInputElement | null;
    if (idEl) idEl.value = id;

    this.resetForm('#modalResetPassword');
    this.getModal('#modalResetPassword')?.show();
  }

  private async handleSubmitPassword(): Promise<void> {
    const root = document.querySelector('#modalResetPassword');
    if (!root) return;

    const id = Number((root.querySelector('[data-password-id]') as HTMLInputElement).value);
    const password = (root.querySelector('[data-field="nuova-password"]') as HTMLInputElement).value;
    const conferma = (root.querySelector('[data-field="conferma-password"]') as HTMLInputElement).value;

    if (password.length < 6) {
      this.mostraValidationError(root, 'La password deve avere almeno 6 caratteri');
      return;
    }
    if (password !== conferma) {
      this.mostraValidationError(root, 'Le password non coincidono');
      return;
    }

    const result = await this.api.utenti.resetPassword(id, password);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    this.getModal('#modalResetPassword')?.hide();
    Weext.toast('Password reimpostata', { type: 'success' });
    this.caricaLista();
  }

  // --- Toggle Stato ---

  private async handleToggleStato(e: Event): Promise<void> {
    const btn = (e.target as HTMLElement).closest('[data-action="toggle-stato"]') as HTMLElement;
    const id = Number(btn?.dataset.id);
    const attivo = btn?.dataset.attivo === '1';
    const nome = btn?.dataset.nome ?? '';
    if (!id) return;

    const azione = attivo ? 'disattivare' : 'attivare';
    const ok = await Weext.confirm(`Vuoi ${azione} l'utente ${nome}?`, {
      title: `Conferma ${azione}`,
      confirmText: attivo ? 'Disattiva' : 'Attiva',
      confirmClass: attivo ? 'btn-danger' : 'btn-success',
    });
    if (!ok) return;

    const result = await this.api.utenti.cambiaStato(id, !attivo);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    Weext.toast(`Utente ${attivo ? 'disattivato' : 'attivato'}`, { type: 'success' });
    this.caricaLista();
  }

  // --- Sync Fenice ---

  private async apriModaleSync(): Promise<void> {
    const modal = this.getModal('#modalSyncFenice');
    if (!modal) return;

    const root = document.querySelector('#modalSyncFenice');
    if (!root) return;

    // Reset stato
    root.querySelector('[data-sync-loading]')?.classList.remove('d-none');
    root.querySelector('[data-sync-error]')?.classList.add('d-none');
    root.querySelector('[data-sync-content]')?.classList.add('d-none');
    const btn = root.querySelector('[data-action="conferma-sync"]') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;

    modal.show();

    const result = await this.api.utenti.feniceAnteprima();
    root.querySelector('[data-sync-loading]')?.classList.add('d-none');

    if (!result.ok) {
      root.querySelector('[data-sync-error]')?.classList.remove('d-none');
      const msgEl = root.querySelector('[data-sync-error-msg]');
      if (msgEl) msgEl.textContent = result.error;
      return;
    }

    const tbody = root.querySelector('[data-sync-body]') as HTMLElement;
    if (tbody) {
      templateParseToElement('gestione-utenti-sync-righe', tbody, { utenti: result.data.anteprima });
    }

    root.querySelector('[data-sync-content]')?.classList.remove('d-none');
    this.aggiornaContatoreSync();
  }

  private handleSelezionaTutti(checked: boolean): void {
    const root = document.querySelector('#modalSyncFenice');
    if (!root) return;
    const checkboxes = root.querySelectorAll('[data-sync-check]') as NodeListOf<HTMLInputElement>;
    for (const cb of checkboxes) cb.checked = checked;
    this.aggiornaContatoreSync();
  }

  private aggiornaContatoreSync(): void {
    const root = document.querySelector('#modalSyncFenice');
    if (!root) return;
    const checked = root.querySelectorAll('[data-sync-check]:checked');
    const counter = root.querySelector('[data-sync-counter]');
    if (counter) counter.textContent = `${checked.length} selezionati`;
    const btn = root.querySelector('[data-action="conferma-sync"]') as HTMLButtonElement | null;
    if (btn) btn.disabled = checked.length === 0;

    // Aggiorna stato "seleziona tutti"
    const total = root.querySelectorAll('[data-sync-check]');
    const selectAll = root.querySelector('[data-action="seleziona-tutti"]') as HTMLInputElement | null;
    if (selectAll) selectAll.checked = checked.length === total.length && total.length > 0;
  }

  private async handleSync(): Promise<void> {
    const root = document.querySelector('#modalSyncFenice');
    if (!root) return;

    const checked = root.querySelectorAll('[data-sync-check]:checked') as NodeListOf<HTMLInputElement>;
    const ids = Array.from(checked).map(cb => Number(cb.dataset.idFenice));
    if (ids.length === 0) return;

    const ok = await Weext.confirm(`Sincronizzare ${ids.length} utenti da Fenice?`, {
      title: 'Conferma sincronizzazione',
      confirmText: 'Sincronizza',
      confirmClass: 'btn-primary',
    });
    if (!ok) return;

    const result = await this.api.utenti.feniceSync(ids);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    const { creati, aggiornati, errori } = result.data;
    const parti = [];
    if (creati > 0) parti.push(`${creati} creati`);
    if (aggiornati > 0) parti.push(`${aggiornati} aggiornati`);
    if (errori.length > 0) parti.push(`${errori.length} errori`);

    Weext.toast(parti.join(', '), { type: errori.length > 0 ? 'warning' : 'success' });
    this.getModal('#modalSyncFenice')?.hide();
    this.caricaLista();
  }

  // --- Lookup Fenice ---

  private async lookupFeniceNuovo(): Promise<void> {
    const root = document.querySelector('#modalNuovoUtente');
    if (!root) return;

    const emailInput = root.querySelector('[data-field="email"]') as HTMLInputElement | null;
    const idFeniceInput = root.querySelector('[data-field="id-fenice"]') as HTMLInputElement | null;
    if (!emailInput || !idFeniceInput) return;

    const email = emailInput.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (idFeniceInput.value) return; // già valorizzato

    await this.eseguiLookupFenice(root, email, idFeniceInput);
  }

  private async lookupFeniceModifica(root: Element, email: string): Promise<void> {
    const idFeniceInput = root.querySelector('[data-field="modifica-id-fenice"]') as HTMLInputElement | null;
    if (!idFeniceInput || idFeniceInput.value) return; // già valorizzato

    await this.eseguiLookupFenice(root, email, idFeniceInput);
  }

  private async eseguiLookupFenice(root: Element, email: string, idFeniceInput: HTMLInputElement): Promise<void> {
    const statusEl = root.querySelector('[data-fenice-status]') as HTMLElement | null;

    if (statusEl) {
      statusEl.innerHTML = '<i class="bi bi-arrow-repeat spin"></i>';
      statusEl.classList.remove('d-none', 'text-success', 'text-muted');
    }

    const result = await this.api.utenti.feniceLookup(email);
    if (!result.ok) {
      if (statusEl) {
        statusEl.innerHTML = '<i class="bi bi-exclamation-triangle text-warning"></i>';
        statusEl.classList.remove('d-none');
      }
      return;
    }

    if (result.data.fenice) {
      idFeniceInput.value = String(result.data.fenice.id);
      if (statusEl) {
        statusEl.innerHTML = '<i class="bi bi-check-lg text-success"></i>';
        statusEl.classList.remove('d-none');
        statusEl.classList.add('text-success');
      }
    } else {
      idFeniceInput.value = '';
      if (statusEl) {
        statusEl.innerHTML = '<i class="bi bi-dash text-muted"></i>';
        statusEl.classList.remove('d-none');
        statusEl.classList.add('text-muted');
      }
    }
  }

  // --- Helpers ---

  private resetForm(modalSelector: string): void {
    const root = document.querySelector(modalSelector);
    if (!root) return;
    for (const input of root.querySelectorAll('input:not([type="radio"]):not([type="hidden"])') as NodeListOf<HTMLInputElement>) {
      input.value = '';
    }
    const statusEl = root.querySelector('[data-fenice-status]') as HTMLElement | null;
    if (statusEl) statusEl.classList.add('d-none');
    this.nascondiValidationError(root);
  }

  private mostraValidationError(root: Element, messaggio: string): void {
    const el = root.querySelector('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private nascondiValidationError(root: Element): void {
    const el = root.querySelector('[data-validation-error]');
    if (!el) return;
    el.textContent = '';
    el.classList.add('d-none');
  }

  protected override onDisconnected(): void {
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    this.modals = [];
    const modali = document.querySelectorAll('[data-modal="nuovo"], [data-modal="modifica"], [data-modal="ruolo"], [data-modal="password"], [data-modal="sync"]');
    for (const el of modali) el.remove();
  }
}
